import React, { useEffect, useRef, useState } from 'react';
import { Carousel } from 'react-responsive-carousel';
import { Icons } from '../../../../assets';
import { Cred } from '../../../../models';
import { UserSkill}  from '../../../../models/skill.model';

const CredCard = ({ cred }: { cred: Cred }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const [hiddenCount, setHiddenCount] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [showAllSkills, setShowAllSkills] = useState(false);
  const [visibleSkills, setVisibleSkills] = useState<UserSkill[]>(cred.skills ?? []);

  const estimateSkillWidth = (text: string) => {
    const averageCharWidth = 8;
    const gap = 8;
    const padding = 32;
    return text.length * averageCharWidth + padding + gap;
  };

  const toggleDescription = () => {
    setIsExpanded(!isExpanded);
  };

  useEffect(() => {
    const el = descriptionRef.current;
    if (el) {
      setIsOverflowing(el.scrollHeight > el.clientHeight + 1);
    }
  }, [cred.description]);

  useEffect(() => {
    if (!cred.skills || showAllSkills) {
      setVisibleSkills(cred.skills ?? []);
      setHiddenCount(0);
      return;
    }

    const containerWidth = containerRef.current?.offsetWidth ?? 0;
    const maxWidth = containerWidth * 2;
    let usedWidth = 0;
    const visible: typeof cred.skills = [];

    for (let i = 0; i < cred.skills.length; i++) {
      const skill = cred.skills[i];
      const skillWidth = estimateSkillWidth(skill.skill.name);
      const remaining = cred.skills.length - (i + 1);
      const moreLabel = `+${remaining} Skills`;
      const moreWidth = remaining > 0 ? estimateSkillWidth(moreLabel) : 0;

      if (remaining > 0 && usedWidth + skillWidth + moreWidth > maxWidth) {
        break;
      }

      usedWidth += skillWidth;
      visible.push(skill);
    }

    setVisibleSkills(visible);
    setHiddenCount(cred.skills.length - visible.length);
  }, [cred, cred.skills, showAllSkills]);

  return (
    <div className="bg-[#F5F5F7] rounded-lg">
      <div className="rounded-lg w-full">
        {cred.resources && cred.resources.length > 0 && (
          <Carousel
            className="w-full"
            emulateTouch
            key={cred.id}
            showIndicators={false}
            showStatus={false}
            showThumbs={false}
            swipeable
            useKeyboardArrows
            onChange={index => {
              videoRefs.current.forEach((video, i) => {
                if (video) {
                  if (i === index) {
                    video.play().catch(() => {});
                  } else {
                    video.pause();
                  }
                }
              });
            }}>
            {cred?.resources.map((item, index) => (
              <div key={index} className="bg-black overflow-hidden pb-[100%] relative rounded-lg w-full">
                {item.type === 'video' ? (
                  <video
                    className="absolute h-full left-0 object-contain top-0 w-full"
                    ref={el => (videoRefs.current[index] = el)}
                    src={item.url}
                    autoPlay
                    controls
                    loop
                    muted
                    playsInline
                  />
                ) : (
                  <img src={item.url} alt="Media" className="absolute h-full left-0 object-contain top-0 w-full" />
                )}
              </div>
            ))}
          </Carousel>
        )}
      </div>
      <button onClick={() => {}} className="p-3 text-left w-full">
        <div className="font-medium mb-2 text-xs text-[#1A1A1A]">
          <div ref={descriptionRef} className={`relative transition-all duration-200 ease-in-out ${isExpanded ? '' : 'line-clamp-2'}`}>
            {cred.description}
            {!isExpanded && isOverflowing && (
              <span
                className="cursor-pointer text-blue-500"
                onClick={toggleDescription}
                style={{
                  position: 'absolute',
                  bottom: '0',
                  right: '0',
                  backgroundColor: '#F5F5F7',
                  paddingLeft: '4px',
                }}>
                ...read more
              </span>
            )}
          </div>
        </div>
        {cred?.skills && cred.skills.length > 0 && (
          <div ref={containerRef} className="flex flex-wrap gap-2 items-center mb-2 overflow-hidden w-full">
            {visibleSkills.map((skill, index) => (
              <button
                disabled
                key={index}
                onClick={() => {}}
                className="bg-[#F2E3FF] justify-center px-4 py-1 rounded-full whitespace-nowrap">
                <span className="font-medium text-xs text-[#1A1A1A]">{skill.skill.name}</span>
              </button>
            ))}
            {hiddenCount > 0 && !showAllSkills && (
              <button
                onClick={() => setShowAllSkills(true)}
                className="bg-white border border-blue-500 justify-center px-4 py-1 rounded-full whitespace-nowrap">
                <span className="font-medium text-blue-600 text-xs">+{hiddenCount} Skills</span>
              </button>
            )}
          </div>
        )}
        <div className="flex items-center justify-between gap-2 mt-2">
          <div className="flex flex-1 flex-wrap gap-2 min-w-0">
            {cred?.organizations &&
              cred.organizations.length > 0 &&
              cred.organizations.map((org, index) => (
                <button
                  disabled
                  key={index}
                  onClick={() => {}}
                  className="bg-[#1A1A1A] flex h-8 items-center justify-center overflow-hidden px-4 py-1 rounded-full">
                  <span className="font-medium text-xs text-white truncate whitespace-nowrap" title={org.organization.name}>
                    {org.organization.name}
                  </span>
                </button>
              ))}
          </div>
          <div className="flex gap-2 items-center">
            <button disabled={cred.hifiCount === 0} onClick={() => {}} className="flex gap-2 items-center">
              <img src={Icons.HI_FI_ICON} alt="Hifi Icon" className="h-8 w-8" />
              <span className="font-semibold text-base text-black">{cred.hifiCount}</span>
            </button>
            <button disabled={cred.reccoCount === 0} onClick={() => {}} className="flex gap-2 items-center">
              <img src={Icons.RECCO_ICON_V2} alt="Recco Icon" className="h-8 w-8" />
              <span className="font-semibold text-base text-black">{cred.reccoCount}</span>
            </button>
          </div>
        </div>
      </button>
    </div>
  );
};

export default CredCard;
